/* eslint-disable no-useless-computed-key */

const BASE_TOKENS = {
    pink: {
        [50]: '#fdf2f8',
        [100]: '#fce7f3',
        [300]: '#f9a8d4',
        [400]: '#f472b6',
        [700]: '#be185d',
        [900]: '#831843',
        [950]: '#500724',
    },
    purple: {
        [50]: '#faf5ff',
        [100]: '#f3e8ff',
        [300]: '#d8b4fe',
        [400]: '#c084fc',
        [700]: '#7e22ce',
        [900]: '#581c87',
        [950]: '#3b0764',
    },
    blue: {
        [50]: '#eff6ff',
        [100]: '#dbeafe',
        [200]: '#bfdbfe',
        [300]: '#93c5fd',
        [400]: '#60a5fa',
        [500]: '#3b82f6',
        [600]: '#2563eb',
        [700]: '#1d4ed8',
        [800]: '#1e40af',
        [900]: '#1e3a8a',
        [950]: '#172554',
    },
    orange: {
        [50]: '#fff7ed',
        [100]: '#ffedd5',
        [200]: '#fed7aa',
        [300]: '#fdba74',
        [400]: '#fb923c',
        [500]: '#f97316',
        [600]: '#ea580c',
        [700]: '#c2410c',
        [800]: '#9a3412',
        [900]: '#7c2d12',
        [950]: '#431407',
    },
    red: {
        [50]: '#fef2f2',
        [100]: '#fee2e2',
        [300]: '#fca5a5',
        [400]: '#f87171',
        [500]: '#ef4444',
        [600]: '#dc2626',
        [700]: '#b91c1c',
        [800]: '#991b1b',
        [900]: '#7f1d1d',
        [950]: '#450a0a',
    },
    grey: {
        [0]: '#FFFFFF',
        [50]: '#f9fafb',
        [100]: '#f3f4f6',
        [200]: '#e5e7eb',
        [300]: '#d1d5db',
        [400]: '#9ca3af',
        [500]: '#6b7280',
        [600]: '#4b5563',
        [700]: '#374151',
        [800]: '#1f2937',
        [900]: '#111827',
        [950]: '#030712',
    },
    slate: {
        [50]: '#f8fafc',
        [100]: '#f1f5f9',
        [200]: '#e2e8f0',
        [300]: '#cbd5e1',
        [600]: '#475569',
    },
    green: {
        [50]: '#f0fdf4',
        [100]: '#dcfce7',
        [300]: '#86efac',
        [400]: '#4ade80',
        [500]: '#22c55e',
        [600]: '#16a34a',
        [700]: '#15803d',
        [800]: '#166534',
        [900]: '#14532d',
        [950]: '#052e16',
    },
    cyan: {
        [100]: '#cffafe',
        [300]: '#67e8f9',
        [400]: '#22d3ee',
        [500]: '#06b6d4',
        [700]: '#0e7490',
        [800]: '#155e75',
        [900]: '#164e63',
        [950]: '#083344',
    },
    yellow: {
        [100]: '#fef9c3',
        [300]: '#fde047',
        [400]: '#facc15',
        [700]: '#a16207',
        [900]: '#713f12',
        [950]: '#422006',
    },
    emerald: {
        [700]: '#047857',
    },
    zinc: {
        [50]: '#fafafa',
        [100]: '#f4f4f5',
        [200]: '#e4e4e7',
        [300]: '#d4d4d8',
        [400]: '#a1a1aa',
        [500]: '#71717a',
        [600]: '#52525b',
        [700]: '#3f3f46',
        [800]: '#27272a',
        [850]: '#202022',
        [900]: '#18181b',
        [950]: '#09090b',
    },
    neutral: {
        [300]: '#d4d4d4',
    },
    sky: {
        [700]: '#0369a1',
        [800]: '#075985',
        [900]: '#0c4a6e',
        [950]: '#082F49',
    },
} as const

export const DESIGN_TOKENS = {
    boxShadow: {
        inputFocus: `0 0 0 0.2rem ${BASE_TOKENS.zinc[850]},
        0 0 0 0.4rem ${BASE_TOKENS.zinc[300]}`,
        toast: '0 10px 15px -3px rgba(0,0,0,.1),0 4px 6px -4px rgba(0,0,0,.1)',
        tab: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
        card: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
        dialog: `0 10px 15px -3px rgba(0, 0, 0, 0.1),
        0 4px 6px -4px rgba(0, 0, 0, 0.1)`,
        popover: `0 4px 6px -1px rgba(0, 0, 0, 0.1),
        0 2px 4px -2px rgba(0, 0, 0, 0.1)`,
    },

    transition: {
        inputHover:
            'color 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    },

    hover: {
        primary: `${BASE_TOKENS.zinc[50]}e6`,
        secondary: BASE_TOKENS.zinc[700],
        destructive: `${BASE_TOKENS.red[900]}e6`,
        confirm: `${BASE_TOKENS.green[900]}e6`,
    },

    background: {
        dark: BASE_TOKENS.zinc[950],
        primary: BASE_TOKENS.zinc[850],
        secondary: BASE_TOKENS.zinc[800],
        muted: BASE_TOKENS.zinc[600],
        light: BASE_TOKENS.zinc[50],
        error: BASE_TOKENS.red[900],
        success: BASE_TOKENS.green[900],
        warning: BASE_TOKENS.orange[900],
        accent: BASE_TOKENS.orange[950],
        map: 'rgba(53, 53, 53)',
    },

    text: {
        foreground: BASE_TOKENS.zinc[50],
        mutedForeground: BASE_TOKENS.zinc[400],
        background: BASE_TOKENS.zinc[900],
        error: BASE_TOKENS.red[500],
        errorLight: BASE_TOKENS.red[50],
        success: BASE_TOKENS.green[500],
        successLight: BASE_TOKENS.green[50],
        info: BASE_TOKENS.blue[500],
        accentForeground: BASE_TOKENS.orange[500],
        warning: BASE_TOKENS.orange[300],
    },

    overlay: {
        dialog: '#000000cc',
    },

    border: {
        input: BASE_TOKENS.zinc[700],
        primary: BASE_TOKENS.zinc[50],
        error: BASE_TOKENS.red[500],
    },

    borderRadius: {
        sm: '0.4rem',
        md: '0.6rem',
        lg: '0.8rem',
        xl: '1.2rem',
        round: '100%',
    },

    wilson: {
        blue: BASE_TOKENS.sky[950],
        scheduled: BASE_TOKENS.sky[800],
        orange: BASE_TOKENS.orange[700],
        grey: BASE_TOKENS.zinc[700],
    },

    pill: {
        background: {
            grey: BASE_TOKENS.zinc[850],
            orange: BASE_TOKENS.orange[950],
            cyan: BASE_TOKENS.cyan[950],
            blue: BASE_TOKENS.blue[950],
            purple: BASE_TOKENS.purple[950],
            green: BASE_TOKENS.green[950],
            red: BASE_TOKENS.red[950],
            yellow: BASE_TOKENS.yellow[950],
            pink: BASE_TOKENS.pink[950],
        },
        border: {
            grey: BASE_TOKENS.zinc[600],
            orange: BASE_TOKENS.orange[700],
            cyan: BASE_TOKENS.cyan[700],
            blue: BASE_TOKENS.blue[700],
            purple: BASE_TOKENS.purple[700],
            green: BASE_TOKENS.green[700],
            red: BASE_TOKENS.red[700],
            yellow: BASE_TOKENS.yellow[700],
            pink: BASE_TOKENS.pink[700],
        },
        text: {
            grey: BASE_TOKENS.zinc[400],
            orange: BASE_TOKENS.orange[400],
            cyan: BASE_TOKENS.cyan[400],
            blue: BASE_TOKENS.blue[400],
            purple: BASE_TOKENS.purple[400],
            green: BASE_TOKENS.green[400],
            red: BASE_TOKENS.red[400],
            yellow: BASE_TOKENS.yellow[400],
            pink: BASE_TOKENS.pink[400],
        },
    },

    lightMode: {
        background: {
            dark: BASE_TOKENS.grey[950],
            primary: 'white',
            secondary: BASE_TOKENS.zinc[100],
            grey: '#E4E4E7',
            map: 'rgba(53, 53, 53)',
        },

        border: {
            input: '#E4E4E7',
        },

        text: {
            foreground: '#09090B',
            mutedForeground: '#71717A',
            accentGreyDark: '#DBE1E9',
            light: '#FAFAFA',
        },

        skeleton: {
            light: BASE_TOKENS.grey[200],
            dark: BASE_TOKENS.grey[300],
        },

        boxShadow: {
            button: '0rem 0.2rem 0.4rem 0rem rgba(0, 0, 0, 0.08)',
            card: '0rem 0.2rem 0.4rem 0rem rgba(0, 0, 0, 0.08), 0px 3px 10px 0px rgba(0, 0, 0, 0.1)',
            popover:
                '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -2px rgba(0, 0, 0, 0.1)',
        },
    },
} as const
